import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { getLinkDataFromUrl } from '@utils/urls';

import Modal from '@common/components/Modal';
import ConfirmationDialog from '@common/components/Modal/ConfirmationDialog';
import { usePrevious } from '@common/hooks/usePrevious';

const DirtyFormConfirmationModal = ({
  isDirty,
  title,
  description,
  dismissText,
  confirmText,
  onBeforeConfirm,
}) => {
  const router = useRouter();
  const previousPathname = usePrevious(router.pathname);
  const { t } = useTranslation();
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [linkData, setLinkData] = useState(null);
  const isDirtyRef = useRef();

  isDirtyRef.current = isDirty;

  const openModal = link => {
    setLinkData(link);
    setConfirmationModalOpen(true);
  };

  const closeModal = () => {
    setConfirmationModalOpen(false);
  };

  const handleRouteChange = useCallback(
    url => {
      const link = url && getLinkDataFromUrl(url);

      if (link?.urlObject.pathname === router.pathname) {
        return;
      }

      if (isDirtyRef.current && link) {
        openModal(link);
      }

      if (isDirtyRef.current) {
        router.events.emit('routeChangeError', { cancelled: true });

        // eslint-disable-next-line no-throw-literal
        throw 'Abort route change due to unsaved changes in form. Please ignore this error.';
      }
    },
    [router.events, router.pathname, isDirtyRef.current],
  );

  const handleConfirmClick = () => {
    isDirtyRef.current = false;

    if (onBeforeConfirm) {
      onBeforeConfirm();
    }

    router.events.off('routeChangeStart', handleRouteChange);
    router.push(linkData.urlObject, linkData.as);
  };

  useEffect(() => {
    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, []);

  useEffect(() => {
    const handler = () => {
      if (previousPathname === router.pathname && isDirtyRef.current === true) {
        window.history.pushState(null, '', previousPathname);
      }
    };

    window.addEventListener('popstate', handler);

    return () => {
      window.removeEventListener('popstate', handler);
    };
  }, [previousPathname, router.pathname]);

  return (
    <Modal
      isOpen={isConfirmationModalOpen}
      onClose={closeModal}
      withCloseButton={false}
      mountOnEnter
      unmountOnExit
      appear
      exit
      enter
      timeout={500}
    >
      <ConfirmationDialog
        onDismiss={closeModal}
        onConfirm={handleConfirmClick}
        title={
          title ||
          t('confirmationDialog.unsavedData.title', 'Änderungen verwerfen??')
        }
        description={
          description ||
          t(
            'confirmationDialog.unsavedData.description',
            'Möchtest Du Deine ungespeicherten Änderungen wirklich verwerfen?',
          )
        }
        dismissText={
          dismissText ||
          t('confirmationDialog.unsavedData.dismiss', 'Abbrechen')
        }
        confirmText={
          confirmText ||
          t('confirmationDialog.unsavedData.confirm', 'Verwerfen')
        }
      />
    </Modal>
  );
};

DirtyFormConfirmationModal.defaultProps = {
  confirmText: undefined,
  description: undefined,
  dismissText: undefined,
  isDirty: false,
  title: undefined,
  onBeforeConfirm: undefined,
};

DirtyFormConfirmationModal.propTypes = {
  confirmText: PropTypes.string,
  description: PropTypes.string,
  dismissText: PropTypes.string,
  isDirty: PropTypes.bool,
  title: PropTypes.string,
  onBeforeConfirm: PropTypes.func,
};

export default React.memo(DirtyFormConfirmationModal);
